import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {
  addAuthListener,
  getAuthState,
  login as apiLogin,
  logout as apiLogout,
  addLoginLoadingListener,
  getLoginLoading,
  addRefreshAuthLoadingListener,
  getRefreshAuthLoading,
  type AuthState,
} from './auth';

type AuthContextValue = {
  token: string | undefined;
  email: string | undefined;
  emailVerified: boolean;
  banned: boolean;
  disabled: boolean;
  activated: boolean;
  betaUser: boolean;
  isAuthenticated: boolean;
  loginLoading: boolean;
  refreshAuthLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const defaultState = getAuthState();

export const AuthContext = React.createContext<AuthContextValue>({
  token: defaultState.token,
  email: defaultState.email,
  emailVerified: Boolean(defaultState?.emailVerified),
  banned: Boolean(defaultState?.banned),
  disabled: Boolean(defaultState?.disabled),
  activated: Boolean(defaultState?.activated),
  betaUser: Boolean(defaultState?.betaUser),
  isAuthenticated: Boolean(defaultState.token),
  loginLoading: getLoginLoading(),
  refreshAuthLoading: getRefreshAuthLoading(),
  async login(_email: string, _password: string) {
    /* Placeholder to please the linter */
  },
  async logout() {
    /* Placeholder to please the linter */
  },
});

export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [state, setState] = React.useState<AuthState>(getAuthState());
  const [loginLoading, setLoginLoading] =
    React.useState<boolean>(getLoginLoading());
  const [refreshAuthLoading, setRefreshAuthLoading] = React.useState<boolean>(
    getRefreshAuthLoading(),
  );

  React.useEffect(() => addAuthListener(setState), []);

  React.useEffect(() => {
    const unsub = addLoginLoadingListener(setLoginLoading);
    return () => {
      unsub();
    };
  }, []);

  React.useEffect(() => {
    const unsub = addRefreshAuthLoadingListener(setRefreshAuthLoading);
    return () => {
      unsub();
    };
  }, []);

  const login = React.useCallback(async (email: string, password: string) => {
    await apiLogin(email, password);
    setState(getAuthState());
  }, []);

  const logout = React.useCallback(async () => {
    await apiLogout();
    setState(getAuthState());
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        email: state.email,
        emailVerified: Boolean(state?.emailVerified),
        banned: Boolean(state.banned),
        disabled: Boolean(state.disabled),
        activated: Boolean(state?.activated),
        betaUser: Boolean(state?.betaUser),
        isAuthenticated: Boolean(state.token),
        loginLoading,
        refreshAuthLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({
  children,
}: {
  children: React.ReactElement;
}): React.JSX.Element {
  const {isAuthenticated} = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return children;
}
